import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  PageContainer,
  StickyBottomContainer,
  BackButton,
  LicensePlate,
  faChevronRight,
  Icon,
  CustomCard,
  Loader,
} from '@/ui/components';
import { useTranslate } from '@/translations';
import { TranslationKey } from '@/translations/types';
import {
  useInspectionsVehicle,
  useInspectionsChecklistDynamicOperations,
  useTNRDetails,
  useInspectionsVehiclesDamagesLines,
  useVehicleDamageClassificationChangeSummary,
} from '@/api/Inspections';
import { InspectionStatusComponent } from '@/modules/Inspection/components/InspectionStatusComponent';

import { App, Button, Card, Col, Empty, Row, Spin, Tabs } from 'antd';
import { useAppCustomers } from '@/hooks';
import { AppRoutes } from '@/routes';
import {
  AppFeatures,
  Inspection,
  InspectionStatusMap,
  VehicleDamageChangeSummary,
} from '@/types';
import { tabs, fields } from './helpers';
import { InspectionCard } from './components/InspectionCard';
import { SalesDecisionCard } from './components/SalesDecisionCard';
import { InspectionPointsTable } from './components/InspectionPointsTable';
import { InspectionStickyCards } from './InspectionStickyCards';
import { estimateFinalGradeFromLines } from './components/tabs/Damage/components/DamageLinePopup/estimateFinalGrade';

export const InspectionPage: FC = () => {
  const { id } = useParams();
  const inspectionId = Number(id);

  const {
    isFetching: isInspectionLoading,
    isError: isInspectionError,
    error: inspectionError,
    data: inspection,
    refetch: refetchInspection,
  } = useInspectionsVehicle(inspectionId);

  if (!id || id.length === 0) return <Empty className="mt-3" />;

  if (isInspectionLoading && !inspection) return <Loader />;

  if (isInspectionError) {
    return (
      <Empty
        className="mt-3"
        description={
          inspectionError instanceof Error
            ? inspectionError.message
            : 'Something went wrong...'
        }
      />
    );
  }

  if (!inspection) return <Empty className="mt-3" />;

  return (
    <InspectionPageContent
      inspection={inspection}
      inspectionId={inspectionId}
      id={id}
      refetchInspection={refetchInspection}
    />
  );
};

type InspectionPageContentProps = {
  inspection: Inspection;
  inspectionId: number;
  id: string;
  refetchInspection: () => void | Promise<unknown>;
};

const InspectionPageContent: FC<InspectionPageContentProps> = ({
  inspection,
  inspectionId,
  id,
  refetchInspection,
}) => {
  const { t } = useTranslate();
  const { modal } = App.useApp();
  const { hasFeatureAccess, hasGbaAccess } = useAppCustomers();

  const [isPopupVisible, setPopupVisibility] = useState(false);
  const [showSticky, setShowSticky] = useState(false);
  const stickyElementRef = React.useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState<string>();
  const [gradePreview, setGradePreview] = useState<
    Record<number, { salesGradeValue: number }>
  >({});

  const {
    data: damages,
    isFetching: damagesLoading,
    refetch: damagesRefech,
  } = useInspectionsVehiclesDamagesLines(inspectionId);

  const isLoading = damagesLoading;

  const ista = useInspectionsChecklistDynamicOperations(inspectionId);
  const TNRdetails = useTNRDetails(inspectionId);

  const togglePopup = useCallback(() => {
    setPopupVisibility(!isPopupVisible);
  }, [isPopupVisible]);

  const mechanicalInspection = ista?.data?.[0];
  const tnr = TNRdetails?.data?.entities?.[0];

  useEffect(() => {
    const elem = stickyElementRef.current;
    if (!elem || typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(([entry]) => {
      setShowSticky(!entry.isIntersecting);
    });
    observer.observe(elem);

    return () => observer.disconnect();
  }, []);

  const classificationChangeSummaryStorageKey = (inspectionId: number) =>
    `damage-classification-summary-dismissed-${inspectionId}`;

  const showClassificationChangeSummaryModal = useCallback(
    (summaries: VehicleDamageChangeSummary[]) => {
      if (!summaries.length) return;

      modal.info({
        title: 'Damage classification change',
        content: (
          <DamageClassificationChangeSummaryContent summaries={summaries} />
        ),
        width: 560,
        okText: t('ok'),
      });
    },
    [modal, t],
  );

  const showGbaClassificationSummary =
    hasGbaAccess &&
    inspection.inspectionStatusName === InspectionStatusMap.RequiresReview;

  const { data: classificationChangeSummary } =
    useVehicleDamageClassificationChangeSummary(
      inspectionId,
      showGbaClassificationSummary,
    );

  useEffect(() => {
    if (
      !inspectionId ||
      !showGbaClassificationSummary ||
      !classificationChangeSummary?.length
    ) {
      return;
    }

    const storageKey = classificationChangeSummaryStorageKey(inspectionId);
    if (sessionStorage.getItem(storageKey)) return;

    sessionStorage.setItem(storageKey, '1');
    showClassificationChangeSummaryModal(classificationChangeSummary);
  }, [
    classificationChangeSummary,
    inspectionId,
    showClassificationChangeSummaryModal,
    showGbaClassificationSummary,
  ]);

  const inspectionPermissions = inspection.permissions;
  const canEditDamage = inspectionPermissions?.canEditDamage ?? false;
  const canEditFr = inspectionPermissions?.canEditFr ?? false;
  const canEditInspection = inspectionPermissions?.canEditInspection ?? false;
  const canChangeStatus = inspectionPermissions?.canChangeStatus ?? false;
  const nextStatusList = inspectionPermissions?.nextStatuses ?? [];
  const hasUnresolvedPtReview = (damages || []).some(
    (damage) => !!damage.ptReviewRequired && !damage.isDeleted,
  );

  const inspectionStatusData = {
    inspectionStatusName: inspection.inspectionStatusName,
    approvalDateTime: inspection.approvalDateTime,
  } as Inspection;

  const showInspectionDetailCard = hasFeatureAccess(
    AppFeatures.InspectionDetailCard,
  );
  const showTnrStatusCard = hasFeatureAccess(
    AppFeatures.InspectionTnrStatusCard,
  );
  const finalGradeEstimate = useMemo(() => {
    const lines = (damages ?? []).flatMap((damage) => {
      const preview = gradePreview[damage.id];
      if (!preview) return [];
      return [
        {
          currentSalesGradeValue: damage.salesGradeValue,
          nextSalesGradeValue: preview.salesGradeValue,
        },
      ];
    });
    if (lines.length === 0) return undefined;
    return estimateFinalGradeFromLines({
      finalGradeScore: inspection.finalGradeScore,
      finalGradePoints: inspection.finalGradePoints,
      lines,
    });
  }, [
    damages,
    gradePreview,
    inspection.finalGradePoints,
    inspection.finalGradeScore,
  ]);
  return (
    <>
      <StickyBottomContainer
        $sticky={112}
        $show={showSticky ? true : undefined}
        className="d-flex align-items-center flex-nowrap gap-3"
        data-testid="inspection-sticky-banner"
        data-visible={showSticky ? 'true' : 'false'}
      >
        <div className="flex-shrink-0">
          <LicensePlate size="large" regNumber={inspection.regNumber} />
        </div>
        <InspectionStickyCards
          panels={[
            {
              key: 'vehicle',
              label: t('vehicle'),
              content: (
                <InspectionCard
                  data={inspection}
                  fields={fields.bannerBasic}
                  column={1}
                  layout="horizontal"
                  size="small"
                  canEdit={canEditInspection}
                />
              ),
            },
            ...(showInspectionDetailCard
              ? [
                  {
                    key: 'inspection',
                    label: t('inspection'),
                    content: (
                      <InspectionCard
                        data={inspection}
                        fields={fields.bannerInspection}
                        column={1}
                        layout="horizontal"
                        size="small"
                        canEdit={canEditInspection}
                      />
                    ),
                  },
                ]
              : []),
            {
              key: 'grade',
              label: t('gradePoints'),
              content: (
                <InspectionPointsTable
                  data={inspection}
                  estimatedFinalGradePoints={finalGradeEstimate?.latestPoints}
                  estimatedFinalGradeScore={finalGradeEstimate?.latestScore}
                />
              ),
            },
          ]}
        />
      </StickyBottomContainer>
      <Spin spinning={isLoading}>
        <PageContainer $sticky={showSticky ? 112 : undefined}>
          <BackButton title={t('inspection')} linkTo={AppRoutes.inspections} />
          <Row gutter={[16, 16]} ref={stickyElementRef}>
            <Col lg={showInspectionDetailCard ? 8 : 12} sm={24}>
              <Card className="h-100">
                <InspectionCard
                  data={inspection}
                  fields={fields.basic}
                  canEdit={canEditInspection}
                />
              </Card>
            </Col>
            {showInspectionDetailCard ? (
              <Col lg={8} sm={24}>
                <Card className="h-100">
                  <InspectionCard
                    data={inspection}
                    fields={fields.inspection}
                    canEdit={canEditInspection}
                  />
                </Card>
              </Col>
            ) : null}
            <Col lg={showInspectionDetailCard ? 8 : 12} sm={24}>
              <Card className="h-100">
                <CustomCard direction="row" className="mb-2">
                  <InspectionCard
                    data={inspectionStatusData}
                    fields={fields.inspectionStatus}
                    column={1}
                  />
                  <Button
                    type={isPopupVisible ? 'default' : 'primary'}
                    onClick={togglePopup}
                    disabled={
                      !canChangeStatus ||
                      nextStatusList.length === 0 ||
                      hasUnresolvedPtReview
                    }
                    title={
                      hasUnresolvedPtReview
                        ? t('ptReviewBlocksProgress')
                        : undefined
                    }
                  >
                    Change Status
                    <Icon icon={faChevronRight} />
                  </Button>
                </CustomCard>
                <InspectionPointsTable
                  data={inspection}
                  estimatedFinalGradePoints={finalGradeEstimate?.latestPoints}
                  estimatedFinalGradeScore={finalGradeEstimate?.latestScore}
                />
              </Card>
            </Col>
          </Row>

          {showTnrStatusCard ? (
            <SalesDecisionCard
              inspection={inspection}
              mechanicalInspection={mechanicalInspection}
              tnr={tnr}
            />
          ) : null}

          <Card>
            <Tabs
              defaultActiveKey="1"
              className="custom-tabs"
              items={tabs
                .filter((o) => hasFeatureAccess(o.feature))
                .map(({ title, component: Component, feature }) => ({
                  key: title,
                  label: t(title as TranslationKey),
                  children: (
                    <Component
                      inspection={inspection}
                      dataSource={
                        feature === AppFeatures.DamageTab ? damages : undefined
                      }
                      onReload={() => {
                        const reloads: Array<Promise<unknown> | unknown> = [
                          refetchInspection(),
                        ];
                        if (feature === AppFeatures.DamageTab) {
                          reloads.push(damagesRefech());
                        }
                        return Promise.all(reloads);
                      }}
                      canEditInspection={canEditInspection}
                      canEditDamage={canEditDamage}
                      canEditFr={canEditFr}
                      canEditParts={inspectionPermissions?.canEditParts}
                      canApproveDamage={inspectionPermissions?.canApproveDamage}
                      canChangePartComponent={
                        inspectionPermissions?.canChangePartComponent
                      }
                      canChangeDamageType={
                        inspectionPermissions?.canChangeDamageType
                      }
                      canChangeSeverity={
                        inspectionPermissions?.canChangeSeverity
                      }
                      canChangeRepairMethod={
                        inspectionPermissions?.canChangeRepairMethod
                      }
                      canChangeWorkCenter={
                        inspectionPermissions?.canChangeWorkCenter
                      }
                      canRemoveDamageLine={
                        inspectionPermissions?.canRemoveDamageLine
                      }
                      onGradePreviewChange={
                        feature === AppFeatures.DamageTab
                          ? setGradePreview
                          : undefined
                      }
                      active={activeTab === title}
                    />
                  ),
                }))}
              onChange={(key) => setActiveTab(key)}
            />
          </Card>
        </PageContainer>
      </Spin>
      <InspectionStatusComponent
        inspectionId={id}
        isPopupVisible={isPopupVisible}
        nextStatusList={nextStatusList}
        onTogglePopup={togglePopup}
      />
    </>
  );
};

export const DamageClassificationChangeSummaryContent: React.FC<{
  summaries: VehicleDamageChangeSummary[];
}> = ({ summaries }) => (
  <div className="d-flex flex-column gap-2">
    {summaries.map((summary) => (
      <React.Fragment key={summary.damageLineId}>
        Damage line: {summary.damageLineDescription}
        <ul>
          {summary.severityChange ? (
            <li>Severity: {summary.severityChange}</li>
          ) : null}
          {summary.repairTypeChange ? (
            <li>Repair type: {summary.repairTypeChange}</li>
          ) : null}
          {summary.preTimingRemovedActions.length > 0 ? (
            <li>
              Removed Pre-timing FR/Labour:{' '}
              {summary.preTimingRemovedActions.join(', ')}
            </li>
          ) : null}
          {summary.preTimingRemovedParts.length > 0 ? (
            <li>
              Removed Pre-timing Parts:{' '}
              {summary.preTimingRemovedParts.join(', ')}
            </li>
          ) : null}
        </ul>
      </React.Fragment>
    ))}
  </div>
);
